// マイページ（Issue #36 / #41）。契約状態・権利一覧・支払い履歴・セルフ解約。
//
// **表示は `plan_type` で出し分ける。** 都度購入（#41）は `KeibaSubscription` の行を
// 作らないため `status` が "none" のまま。`status` だけを見ると、お金を払った利用者に
// 「ご契約なし」と出しつつ利用中コンテンツが並ぶ、という矛盾した画面になる。
//
// **解約を問い合わせ限定にしない**（消費者法制上のリスク / 親issue §1.4）。
// コンテンツの変更は予約制ではなく「解約 → 期末後に新プランで契約」なので、その手順を明示する
// （#35 の決定。期中に契約し直すとサーバが 409 で止める）。
import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import {
  BillingUnavailableError,
  cancelSubscription,
  fetchSubscription,
} from '../api/billing';
import { contentLabel } from '../constants/contents';
import PredictionServiceNav from '../components/predictionService/PredictionServiceNav';

// 定期課金の状態（`Subscription.status`）
const STATUS_LABELS = {
  none: 'ご契約なし',
  pending: 'お手続き中',
  active: 'ご契約中',
  past_due: 'お支払い確認中',
  canceled: '解約済み',
};

// 権利の出どころ（`plan_type`）。定期課金以外はここのラベルを使う
const PLAN_TYPE_LABELS = {
  purchase: '都度購入（自動更新なし）',
  manual: 'ご利用中',
  none: 'ご契約なし',
};

const PAPER_SX = {
  p: { xs: 2.5, sm: 3.5 },
  borderRadius: 4,
  color: 'inherit',
  border: '1px solid rgba(92, 160, 219, 0.2)',
  background: 'linear-gradient(180deg, rgba(10,24,41,0.92), rgba(4,10,20,0.95))',
};

const yen = (amount) =>
  amount === null || amount === undefined ? '—' : `${Number(amount).toLocaleString('ja-JP')}円`;

// サーバは TIMESTAMPTZ を返す。表示は JST に寄せる。
const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Asia/Tokyo',
  });
};

function MyPage() {
  const { logout } = useContext(AuthContext);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [noticeMsg, setNoticeMsg] = useState('');

  const load = () => {
    setLoading(true);
    fetchSubscription()
      .then((data) => {
        setSubscription(data);
        setErrorMsg('');
      })
      .catch((error) => {
        setSubscription(null);
        setErrorMsg(
          error instanceof BillingUnavailableError
            ? 'ご契約の管理はただいま準備中です。'
            : error.message
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCancel = async () => {
    setCanceling(true);
    try {
      const data = await cancelSubscription();
      setSubscription(data);
      setNoticeMsg('解約を受け付けました。期間の末日までは引き続きご利用いただけます。');
      setCancelOpen(false);
    } catch (error) {
      setErrorMsg(error.message);
    } finally {
      setCanceling(false);
    }
  };

  const status = subscription?.status || 'none';
  const planType = subscription?.plan_type || 'none';
  const isSubscription = planType === 'subscription';
  const contents = subscription?.contents || [];
  const payments = subscription?.payments || [];
  // 解約できるのは自動更新のある定期課金だけ。都度購入は放っておけば期限で終わる
  const canCancel = isSubscription && ['active', 'past_due', 'pending'].includes(status);
  const stateLabel = isSubscription
    ? STATUS_LABELS[status] || status
    : PLAN_TYPE_LABELS[planType] || planType;
  // 都度購入・手動付与は次回更新日を持たない。期限（無期限なら「無期限」）で見せる
  const expiresLabel =
    planType === 'none'
      ? '—'
      : subscription?.expires_at
        ? `${formatDate(subscription.expires_at)}${
            subscription.days_remaining === null || subscription.days_remaining === undefined
              ? ''
              : `（あと${subscription.days_remaining}日）`
          }`
        : '無期限';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at 15% 0%, rgba(43,132,255,0.22), transparent 28%), radial-gradient(circle at 90% 10%, rgba(50,255,226,0.14), transparent 24%), linear-gradient(180deg, #02060d 0%, #07111d 46%, #03070f 100%)',
        color: '#eef8ff',
        fontFamily: '"Avenir Next", "Yu Gothic", "Hiragino Sans", sans-serif',
      }}
    >
      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 4 } }}>
        <Stack spacing={3}>
          <PredictionServiceNav />

          <Paper sx={PAPER_SX}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1.5}
            >
              <Box>
                <Typography
                  sx={{
                    color: '#71dbff',
                    fontSize: 12,
                    letterSpacing: '0.24em',
                    textTransform: 'uppercase',
                  }}
                >
                  My Page
                </Typography>
                <Typography sx={{ mt: 1, fontSize: { xs: 24, sm: 30 }, fontWeight: 700 }}>
                  ご契約の管理
                </Typography>
              </Box>
              <Button onClick={logout} sx={{ textTransform: 'none', color: '#9ed7ff' }}>
                ログアウト
              </Button>
            </Stack>

            {loading && (
              <Box sx={{ py: 4, display: 'grid', placeItems: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {!loading && errorMsg && (
              <Alert severity="info" sx={{ mt: 3 }}>
                {errorMsg}
              </Alert>
            )}

            {!loading && !errorMsg && subscription && (
              <>
                {noticeMsg && (
                  <Alert severity="success" sx={{ mt: 2.5 }}>
                    {noticeMsg}
                  </Alert>
                )}

                <Stack spacing={1} sx={{ mt: 2.5 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography sx={{ color: 'rgba(203, 229, 245, 0.72)', minWidth: 110 }}>
                      ご利用状態
                    </Typography>
                    <Chip
                      label={stateLabel}
                      size="small"
                      sx={{
                        color: '#eef8ff',
                        borderColor: 'rgba(92, 160, 219, 0.28)',
                        bgcolor: 'rgba(8, 18, 32, 0.72)',
                      }}
                      variant="outlined"
                    />
                    {subscription.cancel_at_period_end && (
                      <Typography sx={{ fontSize: 13, color: '#ffcf6e' }}>
                        期間末で終了予定
                      </Typography>
                    )}
                  </Stack>

                  {isSubscription && (
                    <Stack direction="row" spacing={1.5}>
                      <Typography sx={{ color: 'rgba(203, 229, 245, 0.72)', minWidth: 110 }}>
                        月額
                      </Typography>
                      <Typography>{yen(subscription.amount_jpy)}（税込）</Typography>
                    </Stack>
                  )}

                  {/* 定期課金は次回更新日、都度購入・手動付与は権利が切れる日を見せる */}
                  <Stack direction="row" spacing={1.5}>
                    <Typography sx={{ color: 'rgba(203, 229, 245, 0.72)', minWidth: 110 }}>
                      {isSubscription && !subscription.cancel_at_period_end
                        ? '次回更新日'
                        : 'ご利用期限'}
                    </Typography>
                    <Typography>
                      {isSubscription
                        ? formatDate(subscription.current_period_end)
                        : expiresLabel}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1.5}>
                    <Typography sx={{ color: 'rgba(203, 229, 245, 0.72)', minWidth: 110 }}>
                      ご利用中
                    </Typography>
                    <Typography>
                      {contents.length ? contents.map(contentLabel).join(' / ') : '—'}
                    </Typography>
                  </Stack>

                  {(subscription.pending_contents || []).length > 0 && (
                    <Stack direction="row" spacing={1.5}>
                      <Typography sx={{ color: 'rgba(203, 229, 245, 0.72)', minWidth: 110 }}>
                        お手続き中
                      </Typography>
                      <Typography>
                        {subscription.pending_contents.map(contentLabel).join(' / ')}
                        （決済の確認が取れ次第ご利用いただけます）
                      </Typography>
                    </Stack>
                  )}
                </Stack>

                <Divider sx={{ my: 2.5, borderColor: 'rgba(92, 160, 219, 0.18)' }} />

                <Typography sx={{ fontWeight: 700, mb: 1 }}>お支払い履歴</Typography>
                {payments.length === 0 ? (
                  <Typography sx={{ color: 'rgba(203, 229, 245, 0.72)' }}>
                    お支払いの記録はまだありません。
                  </Typography>
                ) : (
                  <Stack spacing={0.75}>
                    {payments.map((payment) => (
                      <Stack
                        key={payment.provider_charge_id}
                        direction="row"
                        spacing={2}
                        justifyContent="space-between"
                        sx={{ fontSize: 14 }}
                      >
                        <Typography sx={{ fontSize: 14 }}>{formatDate(payment.paid_at)}</Typography>
                        <Typography sx={{ fontSize: 14 }}>{yen(payment.amount_jpy)}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}

                <Divider sx={{ my: 2.5, borderColor: 'rgba(92, 160, 219, 0.18)' }} />

                {/* #262: 有料化をやめたため、新規の申し込み・申し込み直しは受け付けない */}
                <Typography sx={{ fontWeight: 700, mb: 1 }}>新規のお申し込みについて</Typography>
                <Typography sx={{ color: 'rgba(203, 229, 245, 0.72)', fontSize: 14 }}>
                  新規のお申し込み・コンテンツの変更の受付は終了しました。予想はどなたでも無料でご覧いただけます。
                  {isSubscription && '現在のご契約は、解約されるまで同じ内容で継続します。'}
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2.5 }}>
                  {canCancel && !subscription.cancel_at_period_end && (
                    <Button
                      variant="outlined"
                      onClick={() => setCancelOpen(true)}
                      sx={{
                        borderRadius: 999,
                        px: 3,
                        textTransform: 'none',
                        color: '#ffb3b3',
                        borderColor: 'rgba(255, 179, 179, 0.4)',
                      }}
                    >
                      解約する
                    </Button>
                  )}
                </Stack>
              </>
            )}
          </Paper>
        </Stack>
      </Box>

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>ご契約の解約</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            解約すると次回以降のお支払いは発生しません。
            {formatDate(subscription?.current_period_end)} までは引き続きご利用いただけます。
            よろしいですか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>やめる</Button>
          <Button onClick={handleCancel} disabled={canceling} color="error">
            解約する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MyPage;
